import { createContext, FC, PropsWithChildren, useContext } from "react";

// ─── Context ────────────────────────────────────────────────

export interface ReadOnlyContextValue {
  isReadOnly: boolean;
}

export const ReadOnlyContext = createContext<ReadOnlyContextValue>({
  isReadOnly: false,
});

/**
 * Hook for child components to opt-in to read-only behavior
 * without prop drilling. Returns `true` when the parent
 * ReadOnlyGate has canEdit=false.
 */
export function useReadOnly(): boolean {
  return useContext(ReadOnlyContext).isReadOnly;
}

// ─── Props ──────────────────────────────────────────────────

export interface ReadOnlyGateProps extends PropsWithChildren {
  /** When false, shows the read-only banner and sets isReadOnly=true in context */
  canEdit: boolean;
  /** Custom banner text (supports i18n). Falls back to English default. */
  bannerText?: string;
}

// ─── Component ──────────────────────────────────────────────

const DEFAULT_BANNER_TEXT = "Read only — you don't have edit permissions";

const ReadOnlyGate: FC<ReadOnlyGateProps> = ({
  canEdit,
  children,
  bannerText = DEFAULT_BANNER_TEXT,
}) => {
  const isReadOnly = !canEdit;

  return (
    <ReadOnlyContext.Provider value={{ isReadOnly }}>
      {isReadOnly && (
        <div
          role="status"
          aria-live="polite"
          style={{
            position: "sticky",
            top: 0,
            zIndex: 100,
            background: "var(--warning-bg)",
            color: "var(--warning-text)",
            textAlign: "center",
            padding: "0.5rem 1rem",
            fontSize: "0.85rem",
            fontWeight: 600,
            borderBottom: "1px solid var(--warning-border)",
          }}
        >
          {bannerText}
        </div>
      )}
      {children}
    </ReadOnlyContext.Provider>
  );
};

export default ReadOnlyGate;
